import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Inbox, Search, Trash2, Mail, Phone, Download, Copy, Check, X as XIcon } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useContent, t } from "@/hooks/useContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function parseFields(raw) {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.map((f, i) => ({
      key: (f?.key || `field_${i}`).toString(),
      label: f?.label || (f?.key || `Field ${i + 1}`),
      type: f?.type || "text",
    }));
  } catch { return []; }
}

function fieldValueOf(msg, key) {
  const v = msg?.fields?.[key] ?? msg?.[key];
  if (v === undefined || v === null) return "";
  return v;
}

function CellValue({ msg, field }) {
  const v = fieldValueOf(msg, field.key);
  if (field.type === "checkbox") {
    return v ? <Check size={14} className="text-emerald-600 inline" /> : <XIcon size={14} className="text-[#6B7280] inline" />;
  }
  const str = typeof v === "boolean" ? (v ? "Yes" : "No") : String(v || "");
  if (!str) return <span className="text-[#9CA3AF]">—</span>;
  return <span className="line-clamp-2 break-words">{str}</span>;
}

export default function AdminMessages() {
  const nav = useNavigate();
  const { user, logout: doLogout } = useAuth();
  const c = useContent();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  // Columns: derived from current form schema + any legacy keys found in messages
  const schemaFields = useMemo(() => parseFields(t(c, "contact.form_fields", "")), [c]);

  const allFields = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const f of schemaFields) {
      if (seen.has(f.key)) continue;
      seen.add(f.key);
      result.push(f);
    }
    // append keys present in messages but missing from schema (deleted fields keep visible)
    for (const m of messages) {
      const fields = m?.fields || {};
      for (const k of Object.keys(fields)) {
        if (seen.has(k)) continue;
        seen.add(k);
        result.push({ key: k, label: k.replace(/_/g, " ").replace(/^\w/, (ch) => ch.toUpperCase()), type: typeof fields[k] === "boolean" ? "checkbox" : "text" });
      }
      // legacy top-level keys
      for (const k of ["name", "email", "phone", "message"]) {
        if (seen.has(k)) continue;
        if (m[k]) { seen.add(k); result.push({ key: k, label: k, type: k === "message" ? "textarea" : "text" }); }
      }
    }
    return result;
  }, [schemaFields, messages]);

  const load = async () => {
    try {
      const { data } = await api.get("/contact/messages");
      setMessages(data);
    } catch (err) {
      if (err?.response?.status === 401) nav("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user === false) { nav("/admin/login"); return; }
    if (user) load();
  }, [user]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) => {
      const bag = allFields.map((f) => String(fieldValueOf(m, f.key) || ""));
      return bag.some((s) => s.toLowerCase().includes(q));
    });
  }, [messages, search, allFields]);

  const remove = async (m) => {
    const label = fieldValueOf(m, "name") || m.id;
    if (!window.confirm(`Delete message from ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/contact/messages/${m.id}`);
      toast.success("Message deleted");
      if (selected?.id === m.id) setSelected(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const copyText = (v) => {
    if (!v) return;
    navigator.clipboard.writeText(String(v));
    toast.success("Copied");
  };

  const downloadCsv = () => {
    if (filtered.length === 0) return toast.error("No messages to export");
    const header = [...allFields.map((f) => f.label), "Received"];
    const rows = [header];
    for (const m of filtered) {
      rows.push([
        ...allFields.map((f) => {
          const v = fieldValueOf(m, f.key);
          if (typeof v === "boolean") return v ? "Yes" : "No";
          return String(v || "").replace(/\r?\n/g, "  ");
        }),
        fmtDate(m.created_at),
      ]);
    }
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `braw-messages-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filtered.length} messages`);
  };

  const logout = async () => { await doLogout(); nav("/admin/login"); };

  const senderEmail = selected ? (fieldValueOf(selected, "email") || "") : "";
  const senderPhone = selected ? (fieldValueOf(selected, "phone") || "") : "";
  const senderName = selected ? (fieldValueOf(selected, "name") || "Anonymous") : "";
  const bodyMessage = selected ? (fieldValueOf(selected, "message") || "") : "";

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="sticky top-0 z-30 bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-black/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="back-to-console" className="inline-flex items-center gap-2 text-sm font-bold text-[#111]">
              <ArrowLeft size={16} /> Console
            </Link>
            <img src={LOGO_URL} alt="B-Raw" className="h-9 w-auto" />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D9381E]">Owner Console</div>
              <div className="font-display font-bold text-sm text-[#111] leading-none">Contact Messages</div>
            </div>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-full border-black/10">Logout</Button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#111]" data-testid="messages-title">
              <Inbox className="inline -mt-1 mr-2" /> Inbox
            </h1>
            <p className="mt-2 text-sm text-[#6B7280]">
              Newest first · {filtered.length} of {messages.length} shown · {allFields.length} column{allFields.length === 1 ? "" : "s"} tracked
            </p>
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
              <Input
                data-testid="messages-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search any field…"
                className="pl-9 h-10 w-72"
              />
            </div>
            <Button
              variant="outline"
              onClick={downloadCsv}
              disabled={filtered.length === 0}
              data-testid="messages-download"
              className="rounded-full border-black/10 h-10 disabled:opacity-40"
            >
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 bg-white border border-black/10 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {allFields.map((f) => (
                      <TableHead key={f.key} data-testid={`col-${f.key}`}>{f.label}</TableHead>
                    ))}
                    <TableHead>Received</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="messages-table-body">
                  {loading ? (
                    <TableRow><TableCell colSpan={allFields.length + 2} className="text-center py-10 text-[#6B7280]">Loading…</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={allFields.length + 2} className="text-center py-10 text-[#6B7280]">
                      {messages.length === 0 ? "No messages yet." : "No messages match your search."}
                    </TableCell></TableRow>
                  ) : (
                    filtered.map((m) => (
                      <TableRow
                        key={m.id}
                        onClick={() => setSelected(m)}
                        data-testid={`message-row-${m.id}`}
                        className={`cursor-pointer ${selected?.id === m.id ? "bg-[#F3EAD3]" : ""}`}
                      >
                        {allFields.map((f) => (
                          <TableCell
                            key={f.key}
                            data-testid={`cell-${f.key}-${m.id}`}
                            className="max-w-[240px] align-top"
                          >
                            {f.key === "email" && fieldValueOf(m, "email") ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); copyText(fieldValueOf(m, "email")); }}
                                data-testid={`copy-email-${m.id}`}
                                className="inline-flex items-center gap-2 hover:text-[#D9381E]"
                              >
                                {fieldValueOf(m, "email")} <Copy size={12} />
                              </button>
                            ) : (
                              <CellValue msg={m} field={f} />
                            )}
                          </TableCell>
                        ))}
                        <TableCell className="text-xs text-[#6B7280] align-top">{fmtDate(m.created_at)}</TableCell>
                        <TableCell className="text-right align-top">
                          <button
                            onClick={(e) => { e.stopPropagation(); remove(m); }}
                            data-testid={`delete-message-${m.id}`}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-full text-[#D9381E] hover:bg-[#FEE2E2]"
                            aria-label="Delete message"
                          >
                            <Trash2 size={14} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="bg-white border border-black/10 rounded-2xl p-6 h-fit" data-testid="message-detail">
            {selected ? (
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">From</div>
                <div className="mt-1 font-display font-bold text-2xl text-[#111]">{senderName}</div>
                <div className="mt-3 space-y-2 text-sm">
                  {senderEmail && (
                    <a href={`mailto:${senderEmail}`} className="flex items-center gap-2 text-[#111] hover:text-[#D9381E]">
                      <Mail size={14} /> {senderEmail}
                    </a>
                  )}
                  {senderPhone && (
                    <a href={`tel:${senderPhone}`} className="flex items-center gap-2 text-[#111] hover:text-[#D9381E]">
                      <Phone size={14} /> {senderPhone}
                    </a>
                  )}
                </div>
                {bodyMessage && (
                  <>
                    <div className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">Message</div>
                    <p className="mt-2 text-sm text-[#4B5563] whitespace-pre-line leading-relaxed">{bodyMessage}</p>
                  </>
                )}

                {/* All fields dump */}
                <div className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">All fields</div>
                <div className="mt-2 grid gap-2 text-xs">
                  {allFields.map((f) => {
                    const v = fieldValueOf(selected, f.key);
                    if (v === "" || v === null || v === undefined) return null;
                    return (
                      <div key={f.key} className="flex gap-2">
                        <div className="w-28 shrink-0 font-bold text-[#6B7280] uppercase tracking-widest">{f.label}</div>
                        <div className="text-[#111] break-words">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 text-xs text-[#6B7280]">Received {fmtDate(selected.created_at)}</div>
                <div className="mt-5 flex gap-2">
                  {senderEmail && (
                    <a
                      href={`mailto:${senderEmail}?subject=${encodeURIComponent("Re: your note to B-Raw")}`}
                      data-testid="reply-message"
                      className="inline-flex items-center gap-2 bg-[#111] hover:bg-black text-[#F2A900] px-4 py-2 rounded-full text-sm font-bold"
                    >
                      <Mail size={14} /> Reply
                    </a>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => remove(selected)}
                    data-testid="detail-delete"
                    className="rounded-full border-[#D9381E] text-[#D9381E] hover:bg-[#FEE2E2]"
                  >
                    <Trash2 size={14} className="mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-[#6B7280] text-sm">
                Click a message to see the full note.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
